import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, ShieldCheck, MapPin, Award } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const MARKET_IMG = "https://images.unsplash.com/photo-1660330589243-4c640d878052?w=1200";

export default function Marketplace() {
  const { t } = useApp();
  const [installers, setInstallers] = useState([]);
  useEffect(() => { api.get("/installers").then(({ data }) => setInstallers(data)); }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-end">
          <div className="md:col-span-7">
            <div className="label-eco mb-3">Marketplace</div>
            <h1 className="text-5xl font-black tracking-tight text-[#17301C]">{t.marketplace.title}</h1>
            <p className="text-lg text-[#3A4A3E] mt-3">{t.marketplace.subtitle}</p>
          </div>
          <div className="md:col-span-5 rounded-3xl overflow-hidden border-2 border-[#17301C] aspect-video">
            <img src={MARKET_IMG} alt="Installers" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {installers.map((i) => (
            <div key={i.id} className="card-surface p-6" data-testid={`installer-${i.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-[#17301C]">{i.name}</h3>
                    {i.verified && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EAB308] text-[#17301C] font-bold"><ShieldCheck size={11} />{t.marketplace.verified}</span>}
                  </div>
                  <div className="text-sm text-[#6E7A71] flex items-center gap-1"><MapPin size={12} /> {i.city}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-[#EAB308]">
                    <Star size={16} className="fill-current" />
                    <span className="font-mono-nums font-bold text-[#17301C]">{i.rating}</span>
                  </div>
                  <div className="text-xs text-[#6E7A71]">{i.reviews} {t.marketplace.reviews}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {i.specialties.map((s) => (
                  <span key={s} className="px-2 py-1 rounded-full bg-[#EAE7E0] text-xs font-semibold text-[#17301C]">{s}</span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-[#3A4A3E] flex items-center gap-1"><Award size={13} /> {i.years} {t.marketplace.years_activity}</div>
                <QuoteDialog installer={i} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const QuoteDialog = ({ installer }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", monthly_kwh: 300, notes: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, installer_id: installer.id });
      toast.success(t.quote.success);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", address: "", monthly_kwh: 300, notes: "" });
    } catch { toast.error("Error"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-dash" data-testid={`quote-btn-${installer.id}`}>{t.marketplace.request_quote}</button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="text-[#17301C] font-black">{t.quote.title} · {installer.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required data-testid="q-name" placeholder={t.quote.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" />
          <input required data-testid="q-email" type="email" placeholder={t.quote.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" />
          <input required data-testid="q-phone" placeholder={t.quote.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" />
          <input required data-testid="q-address" placeholder={t.quote.address} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp" />
          <input required data-testid="q-kwh" type="number" placeholder={t.quote.monthly_kwh} value={form.monthly_kwh} onChange={(e) => setForm({ ...form, monthly_kwh: parseFloat(e.target.value) })} className="inp" />
          <textarea data-testid="q-notes" placeholder={t.quote.notes} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp min-h-[80px]" />
          <button type="submit" disabled={loading} data-testid="q-submit" className="btn-primary w-full">{loading ? "..." : t.quote.submit}</button>
        </form>
        <style>{`.inp { width:100%; padding: 0.65rem 0.75rem; border-radius: 0.5rem; border: 1px solid #DDE0D8; background: white; font-size: 0.925rem; } .inp:focus { outline: none; border-color: #17301C; box-shadow: 0 0 0 1px #17301C; }`}</style>
      </DialogContent>
    </Dialog>
  );
};
